import { useCallback, useMemo, useState } from 'react';
import { isEqual } from 'lodash';
import { getCurrentMonth, monthToDateRange } from 'utils/date';

import type { IMonthSelection } from 'components/organisms/provider-dashboard';

type ClinicChangePayload = {
  included?: string[];
  parentClinicGuid?: string[];
};

export const useProviderDashboardFilters = () => {
  const [selectedMonth, setSelectedMonth] = useState<IMonthSelection>(() =>
    getCurrentMonth(),
  );
  const dateRange = useMemo<[Date, Date]>(
    () => monthToDateRange(selectedMonth.year, selectedMonth.month),
    [selectedMonth],
  );
  const [clinicGuid, setClinicGuid] = useState<string[]>([]);
  const [parentClinicGuid, setParentClinicGuid] = useState<string[]>([]);
  const [providerGuid, setProviderGuid] = useState<string | undefined>();

  const filterCount = useMemo(() => {
    const currentMonth = getCurrentMonth();
    let count = 0;

    // Check if month has changed from default (current month)
    if (!isEqual(selectedMonth, currentMonth)) count += 1;

    if (clinicGuid.length > 0 || parentClinicGuid.length > 0) count += 1;
    if (providerGuid) count += 1;
    return count;
  }, [selectedMonth, clinicGuid, parentClinicGuid, providerGuid]);

  const handleResetFilters = () => {
    setSelectedMonth(getCurrentMonth());
    setClinicGuid([]);
    setParentClinicGuid([]);
    setProviderGuid(undefined);
  };

  const handleClinicChange = useCallback(
    ({
      included: inc = [],
      parentClinicGuid: parent = [],
    }: ClinicChangePayload) => {
      setClinicGuid(inc);
      setParentClinicGuid(parent);
    },
    [],
  );

  const handleMonthChange = (value: IMonthSelection | null) => {
    if (value) {
      setSelectedMonth(value);
    }
  };

  const handleProviderChange = (guid: string | undefined) => {
    setProviderGuid(guid);
  };

  return {
    selectedMonth,
    dateRange,
    clinicGuid,
    parentClinicGuid,
    providerGuid,
    filterCount,
    onMonthChange: handleMonthChange,
    onClinicChange: handleClinicChange,
    onProviderChange: handleProviderChange,
    onReset: handleResetFilters,
  };
};
